import { useState, useEffect, useCallback } from "react";
import {
    AreaChart,
    BarChart,
    PieChart,
    Area,
    Bar,
    Pie,
    CartesianGrid,
    XAxis,
    YAxis,
    Tooltip,
    Legend,
    Cell
} from "recharts";
import AutoSizer from "react-virtualized-auto-sizer";

import "./style.scss";
import { ThemeContainer } from "../../common/containers/theme/theme.container";

export enum GraphType {
    area,
    bar,
    pie
}

const BACKGROUND_LIGHT_COLOR = "rgba(255, 255, 255, 0.6)";
const BACKGROUND_DARK_COLOR = "rgba(15, 24, 36, 0.6)";
const DEFAULT_LIGHT_COLOR = "#8099BE";
const DEFAULT_DARK_COLOR = "#9AACC7";
const TEXT_LIGHT_COLOR = "#425976";
const TEXT_DARK_COLOR = "#D5DBE7";
const COLOR_1 = "#88C6FF";
const COLOR_2 = "#F29CC4";
const COLOR_3 = "#FFB800";
const COLOR_4 = "#5EB44D";
const COLOR_5 = "#6994F8";
const COLOR_6 = "#9A85FF";
const COLOR_7 = "#F97B60";
const COLOR_8 = "#72CAC3";
const COLOR_9 = "#ABCA72";
const COLOR_10 = "#C99BF6";
// Pie slices are distinguished by hue only; the light palette has <3:1 between
// adjacent fills, so a dark separator stroke gives every slice a >=3:1 boundary
// (WCAG 1.4.11 graphical objects). #243347 clears 3:1 against all ten fills and
// is theme-invariant because the fills themselves do not change between themes.
const SLICE_SEPARATOR = "#243347";

const getColor = (index: number) =>
    index % 10 === 0
        ? COLOR_10
        : index % 9 === 0
          ? COLOR_9
          : index % 8 === 0
            ? COLOR_8
            : index % 7 === 0
              ? COLOR_7
              : index % 6 === 0
                ? COLOR_6
                : index % 5 === 0
                  ? COLOR_5
                  : index % 4 === 0
                    ? COLOR_4
                    : index % 3 === 0
                      ? COLOR_3
                      : index % 2 === 0
                        ? COLOR_2
                        : COLOR_1;

const graphMargin = { top: 10, right: 5, bottom: 20, left: -40 };

const tooltipContentStyle = (color: string) => ({
    border: "none",
    borderRadius: "6px",
    padding: "4px",
    backgroundColor: color
});

const tooltipLabelStyle = (color: string) => ({
    color: color,
    fontSize: "10px",
    fontWeight: "500",
    whiteSpace: "pre-wrap"
});

const tooltipItemStyle = (color: string) => ({
    color: color,
    fontSize: "10px",
    fontWeight: "500",
    whiteSpace: "pre-wrap"
});

const GraphItemBackground = ({ id, color }: { id: string; color: string }) => {
    return (
        <linearGradient id={id} x1="0" y1="0" x2="0" y2="1">
            <stop stopColor={color} />
            <stop offset="1" stopColor={color} stopOpacity="0" />
        </linearGradient>
    );
};

const GraphBackgroundList = () => {
    return (
        <>
            <GraphItemBackground id="background1" color={COLOR_1} />
            <GraphItemBackground id="background2" color={COLOR_2} />
            <GraphItemBackground id="background3" color={COLOR_3} />
            <GraphItemBackground id="background4" color={COLOR_4} />
            <GraphItemBackground id="background5" color={COLOR_5} />
            <GraphItemBackground id="background6" color={COLOR_6} />
            <GraphItemBackground id="background7" color={COLOR_7} />
            <GraphItemBackground id="background8" color={COLOR_8} />
            <GraphItemBackground id="background9" color={COLOR_9} />
            <GraphItemBackground id="background10" color={COLOR_10} />
        </>
    );
};

const AreaGraph = ({
    items,
    lines,
    width,
    height,
    textColor,
    defaultColor,
    backgroundColor,
    handleScrollIntoView
}: {
    items: any[];
    lines: any[];
    width: number;
    height: number;
    textColor: string;
    defaultColor: string;
    backgroundColor: string;
    handleScrollIntoView: (arg1?: number) => void;
}) => {
    return (
        <AreaChart
            data={items}
            width={width}
            height={height}
            margin={graphMargin}
            onClick={({ activeTooltipIndex }) => handleScrollIntoView(activeTooltipIndex)}
        >
            <defs>
                <GraphBackgroundList />
            </defs>
            <CartesianGrid stroke={defaultColor} strokeDasharray="6 6" opacity={0.5} />
            <XAxis fontSize={10} stroke={defaultColor} dataKey="name" />
            <YAxis fontSize={10} stroke={defaultColor} />
            <Tooltip
                wrapperClassName="graph-tooltip"
                labelStyle={tooltipLabelStyle(textColor)}
                itemStyle={tooltipItemStyle(defaultColor)}
                contentStyle={tooltipContentStyle(backgroundColor)}
            />
            <Legend />
            {lines.map((line: any, index: number) => (
                <Area
                    key={line.key}
                    type="monotone"
                    dataKey={line.key}
                    name={line.label}
                    strokeWidth={3}
                    stroke={getColor(index + 1)}
                    fill={`url(#background${index + 1})`}
                    style={{ cursor: "pointer" }}
                />
            ))}
        </AreaChart>
    );
};

const BarGraph = ({
    items,
    bars,
    width,
    height,
    textColor,
    defaultColor,
    backgroundColor,
    handleScrollIntoView
}: {
    items: any[];
    bars: any[];
    width: number;
    height: number;
    textColor: string;
    defaultColor: string;
    backgroundColor: string;
    handleScrollIntoView: (arg1?: number) => void;
}) => {
    return (
        <BarChart
            className="graph--bar"
            data={items}
            width={width}
            height={height}
            margin={graphMargin}
            onClick={({ activeTooltipIndex }) => handleScrollIntoView(activeTooltipIndex)}
        >
            <defs>
                <GraphBackgroundList />
            </defs>
            <CartesianGrid stroke={defaultColor} strokeDasharray="6 6" opacity={0.5} />
            <XAxis fontSize={10} stroke={defaultColor} dataKey="name" />
            <YAxis fontSize={10} stroke={defaultColor} />
            <Tooltip
                wrapperClassName="graph-tooltip"
                labelStyle={tooltipLabelStyle(textColor)}
                itemStyle={tooltipItemStyle(defaultColor)}
                contentStyle={tooltipContentStyle(backgroundColor)}
            />
            <Legend />
            {bars.map((bar: any, index: number) => (
                <Bar
                    key={bar.key}
                    dataKey={bar.key}
                    name={bar.label}
                    strokeWidth={3}
                    stroke={getColor(index + 1)}
                    fill={`url(#background${index + 1})`}
                    radius={[6, 6, 0, 0]}
                    className="graph-item--bar"
                    enableBackground={1}
                />
            ))}
        </BarChart>
    );
};

const PieGraph = ({
    items,
    pies,
    width,
    height,
    textColor,
    defaultColor,
    backgroundColor,
    handleScrollIntoView
}: {
    items: any[];
    pies: any[];
    width: number;
    height: number;
    textColor: string;
    defaultColor: string;
    backgroundColor: string;
    handleScrollIntoView: (arg1?: number) => void;
}) => {
    const outerRadius = height / 2 - 20;
    const innerRadius = outerRadius - 55;

    return (
        <PieChart width={width} height={height} margin={graphMargin}>
            <Tooltip
                wrapperClassName="graph-tooltip"
                labelStyle={tooltipLabelStyle(textColor)}
                itemStyle={tooltipItemStyle(defaultColor)}
                contentStyle={tooltipContentStyle(backgroundColor)}
            />
            <Legend />
            {pies.map((pie: any, index: number) => (
                <Pie
                    key={pie.key}
                    data={items}
                    dataKey={pie.key}
                    name={pie.label}
                    stroke={SLICE_SEPARATOR}
                    strokeWidth={1}
                    fill={getColor(index + 1)}
                    outerRadius={outerRadius}
                    innerRadius={innerRadius}
                >
                    {items.map((item, index) => (
                        <Cell
                            key={`cell-${index}`}
                            name={`${item.name}\n\n${pie.label}`}
                            fill={getColor(index + 1)}
                            stroke={SLICE_SEPARATOR}
                            strokeWidth={1}
                            style={{ outline: "none", cursor: "pointer" }}
                            onClick={() => handleScrollIntoView(index)}
                        />
                    ))}
                </Pie>
            ))}
        </PieChart>
    );
};

export const Graph = ({
    type,
    list,
    query,
    annotation,
    handleScrollIntoView
}: {
    type: GraphType;
    list: any[];
    query: any;
    annotation: any;
    handleScrollIntoView: (arg1?: number) => void;
}) => {
    const { isLightTheme } = ThemeContainer.useContainer();
    const [lines, setLines] = useState<any[]>([]);
    const [items, setItems] = useState<any[]>([]);
    const DEFAULT_COLOR = isLightTheme ? DEFAULT_LIGHT_COLOR : DEFAULT_DARK_COLOR;
    const TEXT_COLOR = isLightTheme ? TEXT_LIGHT_COLOR : TEXT_DARK_COLOR;
    const BACKGROUND_COLOR = isLightTheme ? BACKGROUND_LIGHT_COLOR : BACKGROUND_DARK_COLOR;

    const getTitle = useCallback(
        (key: string, annotation: any) =>
            annotation?.dimensions[key]?.shortTitle ??
            annotation?.measures[key]?.shortTitle ??
            parseTitle(key),
        []
    );

    const parseTitle = (key: string) =>
        (key.split(".").pop() ?? key)
            .split(/[a-z](?=[A-Z])/g)
            .join(" ")
            .toLowerCase()
            .firstLetterToUppercase();

    useEffect(() => {
        if (!lines.length || !list.length) return;

        const keys = Object.keys(list[0]).filter(key => !lines.map(line => line.key).includes(key));
        const namedList = list.map((item: any, index: number) => {
            const fullName =
                keys.length === 1
                    ? `${getTitle(keys[0], annotation)}: ${item[keys[0]]}`
                    : `Row ${index + 1}`;
            const slicedName = fullName.slice(0, 70);
            const name = slicedName.length < fullName.length ? `${slicedName}...` : fullName;

            return {
                ...item,
                name
            };
        });
        const finalList = lines.reduce(
            (result: any[], measures: any) =>
                result.map(item => {
                    const newItem = { ...item };
                    newItem[measures.key] = Number(item[measures.key]);
                    return { ...newItem };
                }),
            namedList
        );

        setItems(finalList);
    }, [list, lines, annotation, getTitle]);

    useEffect(() => {
        setLines(
            query?.measures
                .filter((key: string) => !(annotation?.measures[key]?.meta?.type === "system"))
                .map((key: string) => ({
                    key: key,
                    label: getTitle(key, annotation)
                })) ?? []
        );
    }, [query, annotation, getTitle]);

    // Text alternative for the chart (WCAG 1.1.1). The chart is an SVG with no
    // inherent accessible name; the exact figures live in the data table shown
    // alongside it, so we expose role="img" + a summary describing what is
    // plotted and point users to that table for values.
    const typeName = type === GraphType.area ? "Area" : type === GraphType.bar ? "Bar" : "Pie";
    const seriesLabels = lines
        .map((line: any) => line.label)
        .filter(Boolean)
        .join(", ");
    const chartLabel =
        `${typeName} chart` +
        (seriesLabels ? ` of ${seriesLabels}` : "") +
        ` across ${items.length} data point${items.length === 1 ? "" : "s"}.` +
        " See the accompanying data table for exact values.";

    return (
        <AutoSizer>
            {({ height, width }: { height: number; width: number }) => (
                <div
                    role="img"
                    aria-label={chartLabel}
                    style={{ width, height }}
                    className="graph-a11y-wrapper"
                >
                    {type === GraphType.area ? (
                        <AreaGraph
                            items={items}
                            lines={lines}
                            width={width}
                            height={height}
                            textColor={TEXT_COLOR}
                            defaultColor={DEFAULT_COLOR}
                            backgroundColor={BACKGROUND_COLOR}
                            handleScrollIntoView={handleScrollIntoView}
                        />
                    ) : type === GraphType.bar ? (
                        <BarGraph
                            items={items}
                            bars={lines}
                            width={width}
                            height={height}
                            textColor={TEXT_COLOR}
                            defaultColor={DEFAULT_COLOR}
                            backgroundColor={BACKGROUND_COLOR}
                            handleScrollIntoView={handleScrollIntoView}
                        />
                    ) : (
                        <PieGraph
                            items={items}
                            pies={lines}
                            width={width}
                            height={height}
                            textColor={TEXT_COLOR}
                            defaultColor={DEFAULT_COLOR}
                            backgroundColor={BACKGROUND_COLOR}
                            handleScrollIntoView={handleScrollIntoView}
                        />
                    )}
                </div>
            )}
        </AutoSizer>
    );
};
